import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardHeader, CardBody, Table, Badge, Skeleton } from '../../components/common';
import { reportApi } from '../../services/api';
import './Reports.css';

export default function CycleTimeReport() {
    const { data, isLoading } = useQuery({
        queryKey: ['report-cycle-time'],
        queryFn: () => reportApi.getCycleTime(),
        select: (res) => res.data?.data || res.data || {}
    });

    const cycleData = data || {};
    const summary = cycleData.summary || {
        avgPRtoApproval: 0,
        avgPOCreation: 0,
        avgDelivery: 0,
        avgPayment: 0,
        totalCycleTime: 0
    };
    const byProcess = cycleData.byProcess || [];
    const trends = cycleData.trends || [];

    const formatDays = (days) => {
        if (!days && days !== 0) return '-';
        if (days < 1) return `${Math.round(days * 24)} hours`;
        return `${days.toFixed(1)} days`;
    };

    const getPerformanceStatus = (actual, target) => {
        if (!target) return { variant: 'default', label: 'N/A' };
        const ratio = actual / target;
        if (ratio <= 0.8) return { variant: 'success', label: 'Excellent' };
        if (ratio <= 1.0) return { variant: 'info', label: 'On Track' };
        if (ratio <= 1.2) return { variant: 'warning', label: 'Delayed' };
        return { variant: 'danger', label: 'Critical' };
    };

    const processColumns = [
        {
            key: 'process',
            title: 'Process',
            dataIndex: 'process',
            render: (val) => <span className="process-name">{val}</span>
        },
        {
            key: 'target',
            title: 'Target',
            dataIndex: 'target',
            align: 'right',
            render: (val) => formatDays(val)
        },
        {
            key: 'actual',
            title: 'Actual',
            dataIndex: 'actual',
            align: 'right',
            render: (val) => formatDays(val)
        },
        {
            key: 'count',
            title: 'Transactions',
            dataIndex: 'count',
            align: 'right'
        },
        {
            key: 'status',
            title: 'Performance',
            render: (_, row) => {
                const status = getPerformanceStatus(row.actual, row.target);
                return <Badge variant={status.variant}>{status.label}</Badge>;
            }
        }
    ];

    return (
        <div className="report-page">
            <PageHeader
                title="Cycle Time Analysis"
                subtitle="Measure procurement process efficiency"
                breadcrumbs={[
                    { label: 'Reports', path: '/reports' },
                    { label: 'Cycle Time' }
                ]}
            />

            {/* Summary Cards */}
            <div className="report-summary-cards">
                <Card className="summary-card primary">
                    <CardBody>
                        <Clock size={24} />
                        <div>
                            <span className="summary-value">{formatDays(summary.avgPRtoApproval)}</span>
                            <span className="summary-label">PR to Approval</span>
                        </div>
                    </CardBody>
                </Card>
                <Card className="summary-card info">
                    <CardBody>
                        <Activity size={24} />
                        <div>
                            <span className="summary-value">{formatDays(summary.avgPOCreation)}</span>
                            <span className="summary-label">PO Processing</span>
                        </div>
                    </CardBody>
                </Card>
                <Card className="summary-card warning">
                    <CardBody>
                        <TrendingUp size={24} />
                        <div>
                            <span className="summary-value">{formatDays(summary.avgDelivery)}</span>
                            <span className="summary-label">Avg Delivery Time</span>
                        </div>
                    </CardBody>
                </Card>
                <Card className="summary-card success">
                    <CardBody>
                        <TrendingDown size={24} />
                        <div>
                            <span className="summary-value">{formatDays(summary.totalCycleTime)}</span>
                            <span className="summary-label">Total Cycle Time</span>
                        </div>
                    </CardBody>
                </Card>
            </div>

            {/* Process Breakdown */}
            <Card>
                <CardHeader>
                    <h3>Process Cycle Times</h3>
                    <p>Comparison of target vs actual cycle times by process</p>
                </CardHeader>
                <Table
                    columns={processColumns}
                    data={byProcess}
                    loading={isLoading}
                    emptyText="No data available"
                    rowKey="process"
                />
            </Card>

            {/* Monthly Trends */}
            <Card style={{ marginTop: '1.5rem' }}>
                <CardHeader>
                    <h3>Monthly Trends</h3>
                    <p>Average cycle time trends over the past months</p>
                </CardHeader>
                <Table
                    columns={[
                        { key: 'month', title: 'Month', dataIndex: 'month' },
                        { key: 'avgCycleTime', title: 'Avg Cycle Time', dataIndex: 'avgCycleTime', align: 'right', render: (val) => formatDays(val) },
                        { key: 'transactions', title: 'Transactions', dataIndex: 'transactions', align: 'right' },
                        {
                            key: 'trend',
                            title: 'Trend',
                            dataIndex: 'trend',
                            render: (val) => (
                                <span className={val < 0 ? 'positive' : val > 0 ? 'negative' : ''}>
                                    {val > 0 ? '+' : ''}{val?.toFixed(1) || 0}%
                                </span>
                            )
                        }
                    ]}
                    data={trends}
                    loading={isLoading}
                    emptyText="No trend data available"
                    rowKey="month"
                />
            </Card>
        </div>
    );
}
